let lbTimerId;
let ozTimerId;
let kgTimerId;
let gmTimerId;

// Helper function to get value from nested path (handles array notation like 'packageInfo[0].weight.oz')
const getValueFromPath = (obj: any, path: string): any => {
  // Convert path like 'packageInfo[0].weight.oz' to array ['packageInfo', '0', 'weight', 'oz']
  const keys = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);

  let current = obj;
  for (const key of keys) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
};

export const lbWeightConversion = (form, lbField: string, ozField: string) => (value) => {
  const lb = parseFloat(value);

  if (isNaN(lb)) {
    form.change(lbField, '');
    form.change(ozField, '');
    return;
  }

  const restLb = lb - Math.floor(lb);
  const restOz = restLb * 16;

  form.change(lbField, value);

  if (Number.isInteger(lb)) return;

  if (lbTimerId) {
    clearTimeout(lbTimerId);
  }
  lbTimerId = setTimeout(() => {
    // Get current oz value from form state
    const currentValues = form.getState().values;
    const currentOz = parseFloat(getValueFromPath(currentValues, ozField)) || 0;

    // Add extracted oz to existing oz
    const totalOz = currentOz + restOz;

    // Normalize: if totalOz >= 16, convert to lb
    const additionalLb = Math.floor(totalOz / 16);
    const finalOz = totalOz - additionalLb * 16;
    // Subtract a tiny epsilon so values already at an integer (e.g. 8.00000001) do not round up unexpectedly.
    const roundedOz = Math.ceil(finalOz - 1e-9);

    // Check if rounding results in 16 oz or more, which should be converted to lb
    if (roundedOz >= 16) {
      const extraLb = Math.floor(roundedOz / 16);
      form.change(lbField, Math.floor(lb) + additionalLb + extraLb);
      form.change(ozField, roundedOz % 16);
    } else {
      form.change(lbField, Math.floor(lb) + additionalLb);
      form.change(ozField, roundedOz);
    }

    return;
  }, 1000);
};

export const ozWeightConversion = (form, lbField: string, ozField: string) => (value) => {
  const oz = parseFloat(value);

  if (isNaN(oz)) {
    form.change(ozField, '');
    clearTimeout(ozTimerId);
    return;
  }

  form.change(ozField, value);

  if (ozTimerId) {
    clearTimeout(ozTimerId);
  }
  ozTimerId = setTimeout(() => {
    if (oz >= 16) {
      // Get current lb value from form state
      const currentValues = form.getState().values;
      const currentLb = parseFloat(getValueFromPath(currentValues, lbField)) || 0;

      // Extract lb from oz
      const extractedLb = Math.floor(oz / 16);
      const restOz = oz - extractedLb * 16;
      const roundedRestOz = Number(restOz.toFixed(1));

      // Add extracted lb to existing lb
      const finalLb = currentLb + extractedLb;

      // Check if rounding the remainder results in 16, which should add 1 more lb
      if (roundedRestOz >= 16) {
        form.change(lbField, finalLb + 1);
        form.change(ozField, 0);
      } else {
        form.change(lbField, finalLb);
        form.change(ozField, roundedRestOz);
      }
      return;
    } else {
      form.change(ozField, Number(oz.toFixed(1)));
      return;
    }
  }, 1000);
};

export const kgWeightConversion = (form: any, kgField: string, gField: string) => (value: any) => {
  const kg = parseFloat(value);

  if (isNaN(kg)) {
    form.change(kgField, '');
    form.change(gField, '');
    return;
  }

  const restKg = kg - Math.floor(kg);
  const restG = restKg * 1000;

  form.change(kgField, value);

  if (Number.isInteger(kg)) return;

  if (kgTimerId) {
    clearTimeout(kgTimerId);
  }
  kgTimerId = setTimeout(() => {
    // Get current gm value from form state
    const currentValues = form.getState().values;
    const currentGm = parseFloat(getValueFromPath(currentValues, gField)) || 0;

    // Add extracted grams to existing grams
    const totalGm = currentGm + restG;

    // Normalize: if totalGm >= 1000, convert to kg
    const additionalKg = Math.floor(totalGm / 1000);
    const finalGm = totalGm - additionalKg * 1000;
    const roundedGm = Math.round(finalGm);

    // Check if rounding results in 1000 gm or more, which should be converted to kg
    if (roundedGm >= 1000) {
      const extraKg = Math.floor(roundedGm / 1000);
      form.change(kgField, Math.floor(kg) + additionalKg + extraKg);
      form.change(gField, roundedGm % 1000);
    } else {
      form.change(kgField, Math.floor(kg) + additionalKg);
      form.change(gField, roundedGm);
    }

    return;
  }, 1000);
};

export const gmWeightConversion = (form: any, kgField: string, gField: string) => (value: any) => {
  const gm = parseFloat(value);

  if (isNaN(gm)) {
    form.change(gField, '');
    clearTimeout(gmTimerId);
    return;
  }

  form.change(gField, value);

  if (gmTimerId) {
    clearTimeout(gmTimerId);
  }
  gmTimerId = setTimeout(() => {
    if (gm >= 1000) {
      // Get current kg value from form state
      const currentValues = form.getState().values;
      const currentKg = parseFloat(getValueFromPath(currentValues, kgField)) || 0;

      // Extract kg from grams
      const extractedKg = Math.floor(gm / 1000);
      const restGm = gm - extractedKg * 1000;
      const roundedRestGm = Number(restGm.toFixed(1));

      // Add extracted kg to existing kg
      const finalKg = currentKg + extractedKg;

      // Check if rounding the remainder results in 1000, which should add 1 more kg
      if (roundedRestGm >= 1000) {
        form.change(kgField, finalKg + 1);
        form.change(gField, 0);
      } else {
        form.change(kgField, finalKg);
        form.change(gField, roundedRestGm);
      }
      return;
    } else {
      form.change(gField, Number(gm.toFixed(1)));
      return;
    }
  }, 1000);
};

export const formatWeight = ({
  lb,
  oz,
  kg,
  gm,
}: {
  lb?: number | string;
  oz?: number | string;
  kg?: number | string;
  gm?: number | string;
}) => {
  return {
    lb: parseFloat(Number(lb).toFixed(2)),
    oz: parseFloat(Number(oz).toFixed(2)),
    kg: parseFloat(Number(kg).toFixed(2)),
    gm: parseFloat(Number(gm).toFixed(2)),
  };
};

export const convertOzToLbOz = (
  weightOzInput?: number | string
): { weightLb: number; weightOz: number } => {
  const parsedValue =
    weightOzInput === '' || weightOzInput === undefined || weightOzInput === null
      ? 0
      : Number(weightOzInput);

  if (isNaN(parsedValue) || parsedValue < 0) {
    return { weightLb: 0, weightOz: 0 };
  }

  const totalOz = parsedValue;
  let weightLb = Math.floor(totalOz / 16);
  let remainderOz = Math.round(totalOz - weightLb * 16);

  if (remainderOz === 16) {
    weightLb += 1;
    remainderOz = 0;
  }

  return { weightLb, weightOz: remainderOz };
};

export const convertGmToKgGm = (
  weightGmInput?: number | string
): { weightKg: number; weightGm: number } => {
  const parsedValue =
    weightGmInput === '' || weightGmInput === undefined || weightGmInput === null
      ? 0
      : Number(weightGmInput);

  if (isNaN(parsedValue) || parsedValue < 0) {
    return { weightKg: 0, weightGm: 0 };
  }

  const totalGm = parsedValue;
  let weightKg = Math.floor(totalGm / 1000);
  let remainderGm = Math.round(totalGm - weightKg * 1000);

  if (remainderGm === 1000) {
    weightKg += 1;
    remainderGm = 0;
  }

  return { weightKg, weightGm: remainderGm };
};

export const getWeightOzValue = (
  weightLb: number | string | null | undefined,
  weightOz: number | string | null | undefined
): number => {
  // Handle empty strings and undefined values
  const lbValue =
    weightLb === '' || weightLb === undefined || weightLb === null ? 0 : Number(weightLb);
  const ozValue =
    weightOz === '' || weightOz === undefined || weightOz === null ? 0 : Number(weightOz);

  // Return 0 if either value is NaN (invalid number)
  if (isNaN(lbValue) || isNaN(ozValue)) {
    return 0;
  }

  return lbValue * 16 + ozValue;
};

export const getWeightGmValue = (
  weightKg: number | string | null | undefined,
  weightGm: number | string | null | undefined
): number => {
  // Handle empty strings and undefined values
  const kgValue =
    weightKg === '' || weightKg === undefined || weightKg === null ? 0 : Number(weightKg);
  const gmValue =
    weightGm === '' || weightGm === undefined || weightGm === null ? 0 : Number(weightGm);

  // Return 0 if either value is NaN (invalid number)
  if (isNaN(kgValue) || isNaN(gmValue)) {
    return 0;
  }

  return kgValue * 1000 + gmValue;
};
